import React, { useState, useCallback, useEffect, useContext } from 'react';
import styles from './Canvas.module.css';
import { IconButton } from '@mui/material';
import { IoRefreshOutline, IoChevronBack, IoChevronForward, IoStop } from 'react-icons/io5';
import DefaultPrompts from '../DefaultPrompts/DefaultPrompts';
import CanvasFrame from './CanvasFrame';
import RecordingIndicator from './RecordingIndicator';
import { ScreenRecordingContext } from '../../contexts/ScreenRecordingContext';

const Canvas = ({ 
  onPromptSelect, 
  activeCanvas, 
  onPrevCanvas, 
  onNextCanvas, 
  onCanvasEvent, 
  onImmediateEvent,
  isProcessing, 
  processingType, 
  currentConversation,
  onSubmit
}) => {
  // Track all loaded canvases and focus state
  const [loadedCanvases, setLoadedCanvases] = useState({});
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = React.useRef(null);

  const { isRecording, stopRecording } = useContext(ScreenRecordingContext);
  const [recordingData, setRecordingData] = useState(null);

  // Calculate if navigation buttons should be disabled
  const canvases = currentConversation?.canvas_details?.filter(c => c.type !== 'separator') || [];
  const currentIndex = activeCanvas ? canvases.findIndex(c => c.id === activeCanvas.id) : -1;
  const isPrevDisabled = currentIndex <= 0;
  const isNextDisabled = currentIndex === -1 || currentIndex >= canvases.length - 1;

  // Debug log active canvas changes
  React.useEffect(() => {
    console.log('Active canvas changed:', {
      id: activeCanvas?.id,
      title: activeCanvas?.title,
      hasCode: Boolean(activeCanvas?.code)
    });
  }, [activeCanvas]);

  // Handle focus events
  React.useEffect(() => {
    const handleDocumentClick = (e) => {
      // Check if click is outside canvas container
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  // Update loaded canvases when active canvas changes
  React.useEffect(() => {
    // Skip if no active canvas
    if (!activeCanvas?.id) return;
    
    console.log('Updating loaded canvases for active canvas:', activeCanvas.id);
    
    // Always update the loaded canvases when active canvas changes
    setLoadedCanvases(prev => {
      // Keep all existing canvases
      return {
        ...prev,
        [activeCanvas.id]: {
          ...activeCanvas,
          _lastActive: Date.now(),
          _refreshKey: prev[activeCanvas.id]?._refreshKey || 'initial'
        }
      };
    });
  }, [activeCanvas]);

  // Reset state when the conversation ID changes
  React.useEffect(() => {
    if (currentConversation?.id) { // Check if we have a valid conversation ID
      console.log(`Conversation changed to ID: ${currentConversation.id}, clearing loaded canvases.`);
      setLoadedCanvases({}); // Always clear existing canvases
      setIsFocused(false); // Reset focus as well
    } else {
      // Handle case where conversation becomes null/undefined (e.g., logged out, cleared)
      console.log('Current conversation cleared, clearing loaded canvases.');
      setLoadedCanvases({});
      setIsFocused(false);
    }
  }, [currentConversation?.id]); // Depend only on the conversation ID

  // Clear loaded canvases when conversation title is "New Conversation" and there are no canvases
  React.useEffect(() => {
    // When the conversation changes to a new conversation
    if (currentConversation?.title === 'New Conversation' && 
        (!currentConversation.canvas_details || currentConversation.canvas_details.length === 0)) {
      console.log('New empty conversation detected, clearing canvas container');
      setLoadedCanvases({});
    }
  }, [currentConversation?.title, currentConversation?.canvas_details]);

  // Handle canvas refresh
  const handleRefresh = useCallback(() => {
    if (!activeCanvas?.id) return;
    
    console.log('Refreshing canvas:', activeCanvas.id);
    setLoadedCanvases(prev => ({
      ...prev,
      [activeCanvas.id]: {
        ...prev[activeCanvas.id],
        ...activeCanvas, // Ensure we have latest canvas data
        _refreshKey: `refresh-${Date.now()}`,
        _lastActive: Date.now()
      }
    }));
  }, [activeCanvas]);

  const handleFocus = useCallback(() => {
    console.log('Canvas focused from direct click');
    setIsFocused(true);
  }, []);

  // Handle iframe refresh
  const refreshCanvas = React.useCallback(() => {
    if (!activeCanvas?.id) return;
    
    console.log('Refreshing canvas:', activeCanvas.id);
    setLoadedCanvases(prev => ({
      ...prev,
      [activeCanvas.id]: {
        ...activeCanvas,
        _refreshKey: Date.now(),
        _lastActive: Date.now()
      }
    }));
  }, [activeCanvas]);

  // Handle focus change from canvas frame
  const handleCanvasFrameFocus = useCallback((focused) => {
    console.log('Canvas received focus state from frame:', focused);
    setIsFocused(focused);
  }, []);

  const handlePromptSubmit = (promptText) => {
    // First set the input value through the prop
    onPromptSelect(promptText);
    
    // Use the provided submit handler
    if (onSubmit) {
      // Small delay to ensure the input value is set before submitting
      setTimeout(() => onSubmit(), 0);
    }
  };

  // Handle window message from ScreenShareModal
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'screen_recording_started') {
        setRecordingData(event.data.stream);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleStopRecording = () => {
    if (recordingData) {
      recordingData.getTracks().forEach(track => track.stop());
      setRecordingData(null);
    }
    // Post message to parent to handle the stop recording action
    window.parent.postMessage({ type: 'screen_recording_stopped' }, '*');
  };

  return (
    <div className={styles.canvasContainer}>
      <div className={styles.canvasHeader}>
        <div className={styles.leftSection}>
          <span className={styles.canvasLabel}>Canvas</span>
          <IconButton 
            size="small" 
            className={styles.refreshButton}
            title="Refresh Canvas"
            onClick={handleRefresh}
          >
            <IoRefreshOutline size={16} />
          </IconButton>

          {/* Processing indicator */}
          <div className={`${styles.processingIndicator} ${isProcessing ? styles.active : ''}`}>
            <div className={styles.spinner} />
            <span className={styles.processingText}>
              {processingType === 'canvas' ? 'Generating Canvas...' :
               processingType === 'edit' ? 'Editing Canvas...' :
               processingType === 'conversation' ? 'Creating conversation...' :
               'Processing query...'}
            </span>
          </div>
        </div>

        <div className={styles.canvasTitle}>
          {isRecording && <RecordingIndicator onStopRecording={stopRecording} />}
          <span>{activeCanvas?.title || 'No Canvas Selected'}</span>
        </div>
      </div>
      
      <div 
        className={`${styles.canvasWrapper} ${isFocused ? styles.focused : ''}`}
        ref={containerRef}
        onMouseDown={handleFocus}
      >
        <IconButton 
          className={`${styles.navButton} ${styles.prevButton}`}
          size="small"
          title="Previous canvas"
          onClick={onPrevCanvas}
          disabled={isPrevDisabled}
        >
          <IoChevronBack size={20} />
        </IconButton>

        <div className={styles.canvasContent}>
          <div className={styles.iframeContainer}>
            {/* Render an iframe for each loaded canvas */}
            {Object.entries(loadedCanvases).map(([canvasId, canvas]) => (
              <CanvasFrame
                key={`${canvasId}-${canvas._refreshKey || 'initial'}`}
                canvas={canvas}
                isActive={activeCanvas?.id === Number(canvasId)}
                onEvent={(event) => {
                  console.log('Canvas received event:', event);
                  const eventWithCanvasId = {
                    ...event,
                    canvasId: canvas.id
                  };
                  // Handle general events
                  onCanvasEvent?.(eventWithCanvasId);
                  // Handle immediate events specifically
                  if (event.type === 'immediate') {
                    onImmediateEvent?.(eventWithCanvasId);
                  }
                }}
                onFocusChange={(focused) => handleCanvasFrameFocus(focused)}
              />
            ))}
          </div>
          {/* Show default prompts if no canvas is selected */}
          {(!activeCanvas || Object.keys(loadedCanvases).length === 0) && (
            <DefaultPrompts 
              onPromptSelect={onPromptSelect}
              onPromptSubmit={handlePromptSubmit}
              conversationId={currentConversation?.id}
            />
          )}
        </div>

        <IconButton 
          className={`${styles.navButton} ${styles.nextButton}`}
          size="small"
          title="Next canvas"
          onClick={onNextCanvas}
          disabled={isNextDisabled}
        >
          <IoChevronForward size={20} />
        </IconButton>
      </div>
    </div>
  );
};

export default Canvas;
